import { useEffect, useState, useCallback } from 'react'
import {
  ShieldCheck, RefreshCw, AlertTriangle, Users, FileSearch, ChevronLeft, ChevronRight,
} from 'lucide-react'
import styles from './DRAdminPage.module.css'

const DR_API = import.meta.env.VITE_DR_API_BASE_URL ?? ''

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('dr_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── Types ──────────────────────────────────────────────────────────────────────

type UserRole =
  | 'DR_ADMIN'
  | 'DR_MANAGER'
  | 'PRODUCT_OWNER'
  | 'PLATFORM_ENGINEER'
  | 'SUPPORT_ENGINEER'
  | 'AUDITOR'
  | 'READ_ONLY'

const ROLE_OPTIONS: UserRole[] = [
  'DR_ADMIN',
  'DR_MANAGER',
  'PRODUCT_OWNER',
  'PLATFORM_ENGINEER',
  'SUPPORT_ENGINEER',
  'AUDITOR',
  'READ_ONLY',
]

interface DRUser {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  role: UserRole
  is_active: boolean
  date_joined: string
}

interface AuditLog {
  id: number
  event_type: string
  user_email: string
  user_role: string
  description: string
  ip_address: string
  timestamp: string
  resource_type: string
  resource_id: string
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20

function fmtDate(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

function fmtDateTime(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function fmtRole(role: string): string {
  return role
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

function RoleBadge({ role }: { role: string }) {
  const cls =
    role === 'DR_ADMIN' ? styles.badgePurple :
    role === 'DR_MANAGER' ? styles.badgeBlue :
    role === 'PRODUCT_OWNER' ? styles.badgeTeal :
    role === 'PLATFORM_ENGINEER' ? styles.badgeAmber :
    role === 'SUPPORT_ENGINEER' ? styles.badgeAmber :
    role === 'AUDITOR' ? styles.badgeGreen :
    styles.badgeGray
  return <span className={cls}>{fmtRole(role)}</span>
}

function EventTypeBadge({ type }: { type: string }) {
  return <span className={styles.badgeCode}>{type}</span>
}

function ActiveBadge({ active }: { active: boolean }) {
  return active
    ? <span className={styles.badgeGreen}>Active</span>
    : <span className={styles.badgeGray}>Inactive</span>
}

// ── Users tab ──────────────────────────────────────────────────────────────────

function UsersTab() {
  const [users, setUsers] = useState<DRUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<number | null>(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${DR_API}/api/v1/dr/auth/users/`, { headers: authHeaders() })
      if (!res.ok) throw new Error('API error')
      const data = await res.json()
      setUsers(data.results ?? data)
    } catch {
      setError('Failed to load users.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  const handleRoleChange = async (user: DRUser, newRole: UserRole) => {
    setUpdatingId(user.id)
    try {
      const res = await fetch(`${DR_API}/api/v1/dr/auth/users/${user.id}/`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      })
      if (!res.ok) throw new Error('Failed to update role')
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role: newRole } : u))
    } catch {
      // revert optimistic update silently — row still reflects last known state
    } finally {
      setUpdatingId(null)
    }
  }

  if (loading) {
    return (
      <div className={styles.loadingRow}>
        <RefreshCw size={18} className={styles.spin} />
        <span>Loading users…</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.inlineError}>
        <AlertTriangle size={15} />
        <span>{error}</span>
        <button className={styles.retryBtn} onClick={fetchUsers}>Retry</button>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}><Users size={28} /></div>
        <p>No users found.</p>
      </div>
    )
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Joined</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id}>
              <td className={styles.emailCell}>{u.email || u.username}</td>
              <td>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</td>
              <td>
                <select
                  className={styles.roleSelect}
                  value={u.role}
                  disabled={updatingId === u.id}
                  onChange={e => handleRoleChange(u, e.target.value as UserRole)}
                >
                  {ROLE_OPTIONS.map(r => (
                    <option key={r} value={r}>{fmtRole(r)}</option>
                  ))}
                </select>
              </td>
              <td><ActiveBadge active={u.is_active} /></td>
              <td className={styles.dateCell}>{fmtDate(u.date_joined)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Audit Log tab ──────────────────────────────────────────────────────────────

function AuditLogTab() {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterType, setFilterType] = useState('')
  const [page, setPage] = useState(1)

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${DR_API}/api/v1/dr/audit-log/`, { headers: authHeaders() })
      if (!res.ok) throw new Error('API error')
      const data = await res.json()
      setLogs(data.results ?? data)
    } catch {
      setError('Failed to load audit log.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchLogs() }, [fetchLogs])

  // Unique event types for the filter dropdown
  const eventTypes = Array.from(new Set(logs.map(l => l.event_type))).sort()

  const filtered = filterType ? logs.filter(l => l.event_type === filterType) : logs

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const handleFilterChange = (val: string) => {
    setFilterType(val)
    setPage(1)
  }

  if (loading) {
    return (
      <div className={styles.loadingRow}>
        <RefreshCw size={18} className={styles.spin} />
        <span>Loading audit log…</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.inlineError}>
        <AlertTriangle size={15} />
        <span>{error}</span>
        <button className={styles.retryBtn} onClick={fetchLogs}>Retry</button>
      </div>
    )
  }

  return (
    <div>
      {/* Filter bar */}
      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Filter by Event Type</label>
          <select
            className={styles.filterSelect}
            value={filterType}
            onChange={e => handleFilterChange(e.target.value)}
          >
            <option value="">All types</option>
            {eventTypes.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className={styles.filterCount}>
          {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
        </div>
      </div>

      {logs.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><FileSearch size={28} /></div>
          <p>No audit log entries found.</p>
        </div>
      )}

      {logs.length > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Event Type</th>
                  <th>Resource</th>
                  <th>Description</th>
                  <th>IP Address</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(l => (
                  <tr key={l.id}>
                    <td className={styles.dateCell}>{fmtDateTime(l.timestamp)}</td>
                    <td className={styles.emailCell}>{l.user_email}</td>
                    <td><RoleBadge role={l.user_role} /></td>
                    <td><EventTypeBadge type={l.event_type} /></td>
                    <td>
                      <div className={styles.resourceCell}>
                        <span className={styles.resourceType}>{l.resource_type}</span>
                        {l.resource_id && (
                          <span className={styles.resourceId}>#{l.resource_id}</span>
                        )}
                      </div>
                    </td>
                    <td className={styles.descCell}>{l.description}</td>
                    <td className={styles.ipCell}>{l.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={safePage <= 1}
              >
                <ChevronLeft size={14} /> Previous
              </button>
              <span className={styles.pageInfo}>
                Page {safePage} of {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────

type Tab = 'users' | 'audit'

export function DRAdminPage() {
  const [activeTab, setActiveTab] = useState<Tab>('users')

  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroBreadcrumb}>
            <ShieldCheck size={12} />
            <span>Disaster Recovery</span>
            <span className={styles.breadSep}>/</span>
            <span>Admin</span>
          </div>
          <h1 className={styles.heroTitle}>DR Administration</h1>
          <p className={styles.heroSub}>
            Manage user roles and permissions, and review the full audit trail for all DR
            platform activity.
          </p>
        </div>
        <div className={styles.heroRight}>
          <ShieldCheck size={28} className={styles.heroIcon} />
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className={styles.tabSection}>
        <div className={styles.tabBar}>
          <button
            className={`${styles.tab} ${activeTab === 'users' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <Users size={14} />
            Users &amp; Roles
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'audit' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <FileSearch size={14} />
            Audit Log
          </button>
        </div>

        <div className={styles.tabContent}>
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'audit' && <AuditLogTab />}
        </div>
      </div>
    </div>
  )
}
